/**
 * memberSatellites — turn a laid-out flow into that flow PLUS one satellite per
 * MEMBER AGENT a node relates to (and per operator-added MCP server), in the
 * SAME vocabulary `withModelSatellites` already uses for models: an icon with a
 * caption, joined to the node by a dashed connector.
 *
 * Pure: takes nodes + edges + the resolved inputs, returns new nodes/edges.
 * Nothing is derived here — the join (declaration → binding ledger → row →
 * recursion) happens ONCE, server-side, in
 * `backend/src/services/workflow-composition.js`; the dispatch marker is read
 * by its ONE reader (`utils/dispatchMarker.js`).
 *
 * ── TWO KINDS OF MEMBER, ONE DRAWING, TWO WORDS ─────────────────────────────
 *
 * A connector is an ASSERTION, so its label says what is true of the node:
 *
 *   • `uses`   — the node CONSUMES the member (a `requires` capability the
 *                template names on that node with `uses: ['<alias>']`, which
 *                the backend resolves into `capabilities[].nodes`).
 *   • `starts` — the node STARTS the member (`dispatchesWorkflow`, string or
 *                roster array). This replaced a full node BOX per started
 *                member folded into the DAG before layout: a started agent is
 *                not a step of THIS graph, and drawing it as one made a
 *                seven-member fleet read as a seven-step pipeline. Founder
 *                (2026-08-25): "change this to this" — the same tile-and-
 *                caption a consumed member already gets.
 *
 * ── WHERE A SATELLITE HANGS, and when a CONNECTOR is drawn ──────────────────
 *
 *   • a NODE of this agent consumes/starts it → one satellite per declaring
 *     node, connector and all, in that node's ATTACHMENT LANE (laneGeometry).
 *
 *   • a CHILD AGENT consumes it (`capabilities[].depth ≥ 1`). NOT DRAWN HERE.
 *     It used to hang off that child's member box; the box is gone, and the
 *     parent's canvas has no node that consumes it, so a connector would be
 *     false. It is on the member's own canvas, one click away through that
 *     member's satellite. (Previously `ownerBoxId`.)
 *
 *   • NOBODY NAMES A CONSUMER (`nodes: []`, which is every own `requires`
 *     member until a template ships `uses:`). Then it belongs to the AGENT and
 *     NO CONNECTOR IS DRAWN. An earlier pass anchored these to the start node
 *     and labelled the line `uses`; the founder read exactly what that line
 *     says — "Start uses gbrain" — and it is false. They sit unattached above
 *     the flow instead, where they claim nothing about any step.
 *
 * Operator-added MCP servers are always in that last class, and for them it is
 * the literal truth rather than a fallback: a template is authored before the
 * agent exists, so nothing could ever have named them at node level, and at run
 * time every attached MCP entry is merged into EVERY model node's `mcpServers`.
 *
 * NOTE, and it is why nothing here implies enforcement: that same runtime fact
 * means `uses:` is a declaration of INTENT that drives this drawing and the
 * docs, NOT an injection gate on who may call the server.
 *
 * ── THE LANE ────────────────────────────────────────────────────────────────
 *
 * Seats are handed out in one order per node — model disc (placed by
 * withModelSatellites at seat 0, and counted here so nothing lands on it),
 * then consumed members, then started members — at `lanePitch(k)` for the
 * node's total seat count `k`. The converter stamps that count on the node
 * (`data.laneSeats`, from the declaration) and the layouter reserves room from
 * the same number; when the stamp is absent (hand-built nodes, tests) the
 * count is taken from what is actually attached.
 */
import { childAgentName } from '../../../utils/dispatchMarker';
import { capName, capKey, memberClickHint, linkedAgentUuid } from '../../../utils/agentCapabilities';
import { MODEL_NODE_PREFIX } from './modelSatellites';
import {
  SAT_DX, SAT_DY, SAT_STEP, lanePitch, laneIsCompact,
} from './laneGeometry';

// Re-exported: the edge renderer derives ATTACHMENT_LANE from it.
export { SAT_DY };

export const MEMBER_NODE_PREFIX = '__memberof__';
// The pseudo-parent of the unattached AGENT-level row. A machine id, printed
// nowhere; distinct from every real node id by its leading underscores.
export const AGENT_ROW_PARENT = '__agent';

// The unattached AGENT-level row: above the flow's top-left, clear of it.
const AGENT_ROW_DY = 150;

const CONNECTOR_STYLE = {
  stroke: 'rgba(190, 190, 190, 0.85)', strokeWidth: 1.5, strokeDasharray: '5 4',
};

/**
 * @param {object[]} nodes            laid-out React Flow nodes (model discs included)
 * @param {object[]} edges            laid-out React Flow edges
 * @param {object}   ctx
 * @param {object[]} ctx.capabilities workflow.capabilities (the rollup)
 * @param {object[]} ctx.customMcp    hand-added MCP servers (already deduped)
 * @param {object}   ctx.capReady     capKey → true|false|null
 * @param {object}   ctx.capIcons     capKey → iconUrl
 * @param {object}   ctx.agentIconsByUuid  uuid → iconUrl, for EVERY deployed agent in
 *   the project (the agents list this page already reads). A custom-MCP entry
 *   created by "Connect an agent" names one of them; this is how its disc gets
 *   that agent's own badge instead of a letter tile.
 * @param {Function} ctx.onOpenMember (m) => void
 * @param {Function} ctx.onOpenSettings () => void
 * @param {object}   [ctx.dispatch]   the STARTED members —
 *   {
 *     map:        dispatchNodeMapOf(graph.nodeConfigs): nodeId → [slug, …]
 *     rows:       slug → the child's deployed row {uuid, workflowType,
 *                 displayName, iconUrl} | null (not deployed / unreadable)
 *     ready:      slug → true|false|null   (childIsReady, same as before)
 *     activeRuns: uuid → number            (running + queued, from the
 *                 agents list the Running pill reads)
 *     onOpen:     (row, slug) => void      (deployed → its Activity tab while
 *                                           it is running, else its own page —
 *                                           see agentCapabilities.memberRoute)
 *     onMissing:  (slug) => void           (not deployed)
 *   }
 * @returns {{nodes: object[], edges: object[]}}
 */
export function withMemberSatellites(nodes, edges, ctx) {
  if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, edges };
  const {
    capabilities = [], customMcp = [], capReady = {}, capIcons = {}, agentIconsByUuid = {},
    onOpenMember, onOpenSettings, dispatch = null,
  } = ctx || {};
  const dispatchMap = (dispatch && dispatch.map && typeof dispatch.map === 'object') ? dispatch.map : {};
  const hasDispatch = Object.keys(dispatchMap).length > 0;
  if (capabilities.length === 0 && customMcp.length === 0 && !hasDispatch) return { nodes, edges };

  const byId = new Map(nodes.map((n) => [n.id, n]));

  // ── Plan the lanes first: every seat a node will carry, before placing any,
  // because the pitch is a function of the TOTAL. Seat 0 is the model disc
  // when the node has one (withModelSatellites already placed it at SAT_DX).
  const seats = new Map();       // parentId → [{ key, label, data }]
  const seatsOf = (parentId) => {
    if (!seats.has(parentId)) seats.set(parentId, []);
    return seats.get(parentId);
  };
  const plan = (parentId, key, relation, data) => {
    if (!byId.has(parentId)) return false;
    seatsOf(parentId).push({ key, relation, data });
    return true;
  };
  const hasDisc = new Set();
  for (const n of nodes) {
    if (n && typeof n.id === 'string' && n.id.startsWith(MODEL_NODE_PREFIX)) {
      hasDisc.add(n.id.slice(MODEL_NODE_PREFIX.length));
    }
  }

  // Floating, connected to nothing — see the header.
  const floats = [];
  const float = (key, data) => floats.push({ key, data });

  // CONSUMED members (`uses`).
  for (const m of capabilities) {
    if (!m || !m.slug) continue;
    // Held by a CHILD agent: not this agent's to call, and with no member box
    // on this canvas there is no node here that consumes it — so no connector
    // can be true. It is drawn on the canvas of the agent that holds it.
    if (m.depth) continue;
    const key = capKey(m);
    const data = {
      name: capName(m),
      iconUrl: capIcons[key] || '',
      notReady: capReady[key] === false,
      onNodeClick: onOpenMember ? () => onOpenMember(m) : null,
    };
    const consumers = (Array.isArray(m.nodes) ? m.nodes : []).filter((nid) => byId.has(nid));
    if (consumers.length) {
      for (const nid of consumers) {
        plan(nid, key, 'uses', { ...data, tooltip: `${data.name} is attached to this step. Click to open it.` });
      }
    } else {
      float(key, {
        ...data,
        tooltip: `${data.name} is attached to this agent — every step can use it. Click to open it.`,
      });
    }
  }

  // STARTED members (`starts`) — one per declared slug, in declaration order,
  // under the node that declares it. The same slug under two dispatchers gets
  // a satellite under EACH: deduping across nodes would silently unwire one.
  if (hasDispatch) {
    const rows = (dispatch.rows && typeof dispatch.rows === 'object') ? dispatch.rows : {};
    const ready = (dispatch.ready && typeof dispatch.ready === 'object') ? dispatch.ready : {};
    const runs = (dispatch.activeRuns && typeof dispatch.activeRuns === 'object') ? dispatch.activeRuns : {};
    for (const [nid, slugs] of Object.entries(dispatchMap)) {
      if (!Array.isArray(slugs)) continue;
      for (const slug of slugs) {
        const row = rows[slug] || null;
        const deployed = !!(row && row.workflowType);
        const name = childAgentName(row, slug);
        const activeRuns = deployed && row.uuid ? Math.max(0, Number(runs[row.uuid]) || 0) : 0;
        plan(nid, `starts:${slug}`, 'starts', {
          name,
          iconUrl: (deployed && row.iconUrl) || '',
          // Not deployed here, or deployed but unable to run (its own model /
          // key), read the same way: this step cannot start it yet.
          notReady: !deployed || ready[slug] === false,
          activeRuns,
          tooltip: !deployed
            ? `${name} (${slug}) is started by this step but isn't deployed in this project yet.`
            : activeRuns > 0
              ? `${name} is started by this step — ${activeRuns} run${activeRuns === 1 ? '' : 's'} in flight. Click to open its activity.`
              // IDLE goes to the member's OWN page, not its activity (founder,
              // 2026-08-26) — there are no runs to disambiguate. The words come
              // from the one helper the route reads, so they cannot drift.
              : `${name} is started by this step. ${memberClickHint(0)}`,
          onNodeClick: deployed
            ? (dispatch.onOpen ? () => dispatch.onOpen(row, slug) : null)
            : (dispatch.onMissing ? () => dispatch.onMissing(slug) : null),
        });
      }
    }
  }

  for (const srv of customMcp) {
    if (!srv) continue;
    // The NAME is the label. Never the url and never the id — a raw
    // `717DCCEE21608860CEBB608374` or a host string on a customer's canvas is
    // the engineer-only label this whole pass exists to remove.
    const nm = srv.name || 'MCP server';
    // THE ICON. An entry someone typed by hand points at a THIRD-PARTY server:
    // we have no icon for it and it takes the letter tile, which is honest. But
    // an entry created by Settings → "Connect an agent" points at a SIBLING
    // AGENT IN THIS PROJECT, and the link stamped that agent's uuid onto the
    // row (`source: 'agent:<uuid>'`) — so its badge is DERIVED, not guessed.
    // Unknown uuid (an agent since deleted, or a genuinely external server) ⇒
    // letter tile, exactly as before.
    const linked = linkedAgentUuid(srv);
    float(`mcp:${srv.id || nm}`, {
      name: nm,
      iconUrl: (linked && agentIconsByUuid[linked]) || '',
      notReady: false,
      tooltip: `${nm} is a custom MCP server added to this agent. Click to open its settings.`,
      onNodeClick: onOpenSettings || null,
    });
  }

  // ── Place. Seat i of a k-seat lane sits at SAT_DX + i · lanePitch(k).
  const satNodes = [];
  const satEdges = [];
  for (const [parentId, list] of seats) {
    const parent = byId.get(parentId);
    const discSeats = hasDisc.has(parentId) ? 1 : 0;
    const actual = discSeats + list.length;
    // The converter's stamp is the DECLARED count; the layouter reserved room
    // from it, so the pitch follows it. Absent → what is actually here.
    const stamped = Number(parent.data?.laneSeats);
    const k = Number.isFinite(stamped) && stamped > 0 ? Math.max(stamped, actual) : actual;
    const pitch = lanePitch(k);
    const compact = laneIsCompact(k);
    list.forEach((s, i) => {
      const seat = discSeats + i;
      const id = `${MEMBER_NODE_PREFIX}${parentId}__${s.key}`;
      satNodes.push({
        id,
        type: 'memberNode',
        draggable: false,
        selectable: false,
        position: {
          x: (parent.position?.x || 0) + SAT_DX + seat * pitch,
          y: (parent.position?.y || 0) + SAT_DY,
        },
        data: { ...s.data, attached: true, compact },
      });
      satEdges.push({
        id: `e${id}`,
        source: parentId,
        sourceHandle: 'bottom',
        target: id,
        targetHandle: 'disc',
        type: 'smoothstep',
        pathOptions: { borderRadius: 12 },
        focusable: false,
        selectable: false,
        // NO caption. The connector used to print its relation (`uses` /
        // `starts`); founder ruling 2026-08-26: "those are useless to the
        // user". The relation is kept as DATA — it is what the line asserts,
        // and the tooltip still says it — it just is not printed on the line.
        style: CONNECTOR_STYLE,
        data: {
          relation: s.relation,
          // The SAME count the satellite's paper stack is drawn from, on the
          // connector — the edge renderer lights the fibre from it
          // (FibreBeams). One number, stamped once, two readers.
          activeRuns: Math.max(0, Number(s.data.activeRuns) || 0),
        },
      });
    });
  }

  if (floats.length) {
    let minX = Infinity;
    let minY = Infinity;
    for (const n of nodes) {
      if (!n || !n.position) continue;
      if (n.position.x < minX) minX = n.position.x;
      if (n.position.y < minY) minY = n.position.y;
    }
    if (!Number.isFinite(minX)) { minX = 0; minY = 0; }
    floats.forEach((f, i) => {
      satNodes.push({
        id: `${MEMBER_NODE_PREFIX}${AGENT_ROW_PARENT}__${f.key}`,
        type: 'memberNode',
        draggable: false,
        selectable: false,
        position: { x: minX + i * SAT_STEP, y: minY - AGENT_ROW_DY },
        data: { ...f.data, attached: false, compact: false },
      });
    });
  }

  if (satNodes.length === 0) return { nodes, edges };
  return { nodes: [...nodes, ...satNodes], edges: [...edges, ...satEdges] };
}

export default withMemberSatellites;
